// src/pages/common-components/Reservation/ReserveCarModal.tsx
// Replaces the jewellery "Add to Cart" flow for cars. Since a car is a unique
// unit (not a stackable product) and no payment gateway is used, this modal
// simply captures the customer's contact details and intent (reserve the car,
// book a test drive, or ask a general question) and writes it straight to
// Firestore. Staff follow up and record any deposit manually in the admin.
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { submitReservationInquiry, InquiryType } from '../../Services/reservationService';
import { useBrands } from '../../Contexts/BrandContext';
import { useCarModelsByBrand } from '../../Hooks/useCarModelQuery';
import { fetchFilteredProducts, Product } from '../../Services/productService';
import { useAuth } from '../../Contexts/AuthContext';

interface ReserveCarModalProps {
  // Optional — omit both to submit a general inquiry not tied to one car
  // (e.g. the site-wide "Book a Test Drive" CTA).
  carId?: string;
  carName?: string;
  carImage?: string;
  carPrice?: number;
  defaultInquiryType?: InquiryType;
  onClose: () => void;
  // When true, renders the form content directly in the page flow instead of
  // as a fixed-position overlay dialog — used by the "Book a Test Drive" page
  // so the form is visible immediately, with no popup and no close button.
  inline?: boolean;
}

const styles = `
  .rcm-overlay {
    position: fixed; inset: 0; z-index: 9999; background: rgba(10,8,6,0.5);
    animation: rcm-fade-in 0.25s ease;
  }
  .rcm-dialog {
    background: #fff; width: 100%; max-width: 420px; position: fixed;
    top: 0; left: 0; height: 100%; height: 100dvh;
    display: flex; flex-direction: column; box-shadow: 8px 0 24px rgba(0,0,0,0.15);
    animation: rcm-slide-in 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .rcm-dialog--inline {
    max-width: 520px; position: static; height: auto; box-shadow: none;
    background: transparent; animation: none;
  }
  @keyframes rcm-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes rcm-slide-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
  @media (max-width: 480px) {
    .rcm-dialog { max-width: 100%; }
  }

  .rcm-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px; border-bottom: 1px solid #eee; flex-shrink: 0;
  }
  .rcm-dialog--inline .rcm-header { padding: 0 0 18px; border-bottom: none; }
  .rcm-body {
    padding: 20px; overflow-y: auto; flex: 1 1 auto;
  }
  .rcm-dialog--inline .rcm-body { padding: 0; overflow-y: visible; }
  .rcm-footer {
    padding: 16px 20px; border-top: 1px solid #eee; flex-shrink: 0;
  }
  .rcm-dialog--inline .rcm-footer { padding: 18px 0 0; border-top: none; }
  .rcm-close {
    background: #f0f0f5; border: none; border-radius: 50%; width: 38px; height: 38px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; cursor: pointer; color: #6b7280; line-height: 1; flex-shrink: 0;
    transition: all 0.25s ease;
  }
  .rcm-close:hover { background: #717fe0; color: #fff; transform: rotate(90deg); }
  .rcm-title { font-size: 18px; font-weight: 600; margin: 0; }
  .rcm-subtitle { font-size: 14px; color: #777; margin-bottom: 18px; }
  .rcm-tabs { display: flex; gap: 8px; margin-bottom: 18px; flex-wrap: wrap; }
  .rcm-tab {
    padding: 7px 14px; border: 1px solid #ddd; border-radius: 20px; font-size: 13px;
    cursor: pointer; background: #fff; color: #333;
  }
  .rcm-tab.active { background: #222; color: #fff; border-color: #222; }
  .rcm-field { margin-bottom: 14px; }
  .rcm-field label { display: block; font-size: 13px; margin-bottom: 5px; color: #444; }
  .rcm-field input, .rcm-field textarea, .rcm-field select {
    width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 3px;
    font-size: 16px; font-family: inherit; box-sizing: border-box; background: #fff;
  }
  .rcm-field select {
    padding-right: 32px;
    appearance: none; -webkit-appearance: none; -moz-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23666' stroke-width='1.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }
  .rcm-field-row { display: flex; gap: 10px; }
  .rcm-field-row .rcm-field { flex: 1; min-width: 0; }
  .rcm-submit {
    width: 100%; height: 44px; padding: 0 13px; background: transparent; color: #717fe0;
    border: 2px solid #717fe0; border-radius: 100px; font-size: 13px; font-weight: 600;
    letter-spacing: 0.5px; text-transform: uppercase; cursor: pointer; margin-top: 6px;
    transition: all 0.25s ease;
  }
  .rcm-submit:hover:not(:disabled) { background: #717fe0; color: #fff; }
  .rcm-submit:disabled { opacity: 0.6; cursor: not-allowed; }
  .rcm-success { text-align: center; padding: 20px 0; }
  .rcm-success-icon {
    width: 52px; height: 52px; border-radius: 50%; background: #2e7d32; color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 26px;
    margin: 0 auto 16px;
  }
  .rcm-success-details {
    text-align: left; border: 1px solid #eee; border-radius: 6px; padding: 14px 16px;
    margin: 18px 0; background: #fafafa;
  }
  .rcm-success-row {
    display: flex; justify-content: space-between; gap: 12px; font-size: 13px;
    padding: 6px 0; border-bottom: 1px solid #eee; color: #666;
  }
  .rcm-success-row:last-child { border-bottom: none; }
  .rcm-success-row strong { color: #222; text-align: right; }
  .rcm-success-next {
    font-size: 13px; color: #666; text-align: left; background: #f0f2ff;
    border-radius: 6px; padding: 12px 14px; margin: 0;
  }
  .rcm-error { color: #c0392b; font-size: 13px; margin-bottom: 10px; }
`;

const ReserveCarModal: React.FC<ReserveCarModalProps> = ({ carId, carName, carImage, carPrice, defaultInquiryType, onClose, inline = false }) => {
  const { currentUser, customerProfile } = useAuth();
  const isGeneral = !carId;
  // The site-wide "Book a Test Drive" flow (no carId) only ever books a test
  // drive — there's no separate general-inquiry path to choose from, so it's
  // the only tab and the tab selector itself is hidden (see below).
  const availableTabs: InquiryType[] = isGeneral
    ? ['Test Drive Request']
    : ['Purchase / Reservation', 'Test Drive Request', 'General Inquiry'];
  const [inquiryType, setInquiryType] = useState<InquiryType>(defaultInquiryType || availableTabs[0]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from the signed-in customer's profile, if available.
  useEffect(() => {
    if (!currentUser) return;
    setName((prev) => prev || customerProfile?.fullName || currentUser.displayName || '');
    setEmail((prev) => prev || customerProfile?.email || currentUser.email || '');
    setPhone((prev) => prev || customerProfile?.phone || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, customerProfile]);

  // ── Brand / Model / specific-vehicle selection ──────────────────────────
  // Only shown for the general "Book a Test Drive" flow (no carId prop) —
  // customer can narrow to a model, or go further and pick one specific
  // in-stock unit; neither is required (defaults to "any vehicle").
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');

  const { activeBrands } = useBrands();
  const { data: modelsForBrand = [] } = useCarModelsByBrand(selectedBrandId || undefined);

  const { data: vehiclesForModel } = useQuery({
    queryKey: ['test-drive-vehicles', selectedBrandId, selectedModelId],
    queryFn: () => fetchFilteredProducts({ brandId: selectedBrandId, modelId: selectedModelId }, null, 25),
    enabled: isGeneral && !!selectedBrandId && !!selectedModelId,
  });
  const vehicleOptions: Product[] = vehiclesForModel?.products || [];

  const selectedBrand = activeBrands.find(b => b.id === selectedBrandId);
  const selectedModel = modelsForBrand.find(m => m.id === selectedModelId);
  const selectedVehicle = vehicleOptions.find(v => v.id === selectedVehicleId);

  const submittedVehicleLabel = selectedVehicle?.name || carName ||
    (selectedModel ? `${selectedBrand?.name ? selectedBrand.name + ' ' : ''}${selectedModel.name}` : '');

  // Changing the brand invalidates whatever model/vehicle was chosen under
  // the previous brand; changing the model invalidates the specific vehicle.
  useEffect(() => { setSelectedModelId(''); setSelectedVehicleId(''); }, [selectedBrandId]);
  useEffect(() => { setSelectedVehicleId(''); }, [selectedModelId]);

  // Lock body scroll while the drawer overlay is open (not needed for the
  // inline test-drive-page rendering, which has no overlay).
  useEffect(() => {
    if (inline) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarWidth + 'px';
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [inline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError('Please enter your name and phone number.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await submitReservationInquiry({
        inquiryType,
        // A specific vehicle picked from the model's in-stock units takes
        // over the normal single-car fields; otherwise fall back to whatever
        // carId/carName was passed in as a prop (the per-car flow).
        carId: selectedVehicle?.id || carId,
        carName: selectedVehicle?.name || carName,
        carImage: selectedVehicle?.images?.[0] || carImage,
        carPrice: selectedVehicle?.price ?? carPrice,
        brandId: !selectedVehicle ? (selectedBrandId || undefined) : undefined,
        brandName: !selectedVehicle ? (selectedBrand?.name || undefined) : undefined,
        modelId: !selectedVehicle ? (selectedModelId || undefined) : undefined,
        modelName: !selectedVehicle ? (selectedModel?.name || undefined) : undefined,
        customerName: name, customerPhone: phone, customerEmail: email || undefined,
        preferredDate: preferredDate || undefined, preferredTime: preferredTime || undefined, message: message || undefined,
        submittedByUid: currentUser?.uid,
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const dialogContent = (
    <div
      className={`rcm-dialog${inline ? ' rcm-dialog--inline' : ''}`}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : true}
    >
      <div className="rcm-header">
        <h3 className="rcm-title">{submitted ? 'Request Sent' : (carName || 'Book a Test Drive')}</h3>
        {!inline && (
          <button className="rcm-close" onClick={onClose} aria-label="Close">&times;</button>
        )}
      </div>

      <div className="rcm-body">
        {submitted ? (
          <div className="rcm-success">
            <div className="rcm-success-icon">&#10003;</div>
            <p className="rcm-subtitle" style={{ textAlign: 'center' }}>
              Thank you, {name}! We've received your {inquiryType.toLowerCase()}
              {submittedVehicleLabel ? <> for <strong>{submittedVehicleLabel}</strong></> : ''}.
              Our team will contact you shortly at {phone}.
            </p>
            <div className="rcm-success-details">
              <div className="rcm-success-row"><span>Request Type</span><strong>{inquiryType}</strong></div>
              {submittedVehicleLabel && <div className="rcm-success-row"><span>Vehicle</span><strong>{submittedVehicleLabel}</strong></div>}
              <div className="rcm-success-row"><span>Contact Number</span><strong>{phone}</strong></div>
              {email && <div className="rcm-success-row"><span>Email</span><strong>{email}</strong></div>}
              {inquiryType === 'Test Drive Request' && preferredDate && (
                <div className="rcm-success-row"><span>Preferred Date</span><strong>{preferredDate}</strong></div>
              )}
              {inquiryType === 'Test Drive Request' && preferredTime && (
                <div className="rcm-success-row"><span>Preferred Time</span><strong>{preferredTime}</strong></div>
              )}
            </div>
            <p className="rcm-success-next">
              <strong>What happens next?</strong> A member of our sales team will reach out within one business day to confirm the details.
            </p>
          </div>
        ) : (
          <>
            <p className="rcm-subtitle">
              {isGeneral
                ? "Tell us what you're interested in and we'll set up a test drive — no payment is required to submit this form."
                : 'Let us know how we can help — no payment is required to submit this form.'}
            </p>

            {availableTabs.length > 1 && (
              <div className="rcm-tabs">
                {availableTabs.map(t => (
                  <div
                    key={t}
                    className={`rcm-tab ${inquiryType === t ? 'active' : ''}`}
                    onClick={() => setInquiryType(t)}
                  >
                    {t}
                  </div>
                ))}
              </div>
            )}

            {error && <div className="rcm-error">{error}</div>}

            <form id="rcm-form" onSubmit={handleSubmit}>
              {isGeneral && (
                <div className="rcm-field-row">
                  <div className="rcm-field">
                    <label>Brand (optional)</label>
                    <select value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                      <option value="">Any brand</option>
                      {activeBrands.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rcm-field">
                    <label>Model (optional)</label>
                    <select
                      value={selectedModelId}
                      onChange={(e) => setSelectedModelId(e.target.value)}
                      disabled={!selectedBrandId}
                    >
                      <option value="">Any model</option>
                      {modelsForBrand.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {isGeneral && selectedModelId && (
                <div className="rcm-field">
                  <label>Specific vehicle (optional)</label>
                  <select value={selectedVehicleId} onChange={(e) => setSelectedVehicleId(e.target.value)}>
                    <option value="">Any available {selectedModel?.name || 'unit'}</option>
                    {vehicleOptions.map(v => {
                      const details = [
                        v.exteriorColor,
                        v.modelYear,
                        v.mileageKm != null ? `${v.mileageKm.toLocaleString()} km` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <option key={v.id} value={v.id}>{v.name}{details ? ` (${details})` : ''}</option>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="rcm-field">
                <label>Full Name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div className="rcm-field">
                <label>Phone Number *</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07X XXX XXXX" />
              </div>
              <div className="rcm-field">
                <label>Email (optional)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              {inquiryType === 'Test Drive Request' && (
                <div className="rcm-field-row">
                  <div className="rcm-field">
                    <label>Preferred Date</label>
                    <input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} />
                  </div>
                  <div className="rcm-field">
                    <label>Preferred Time</label>
                    <input type="time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} />
                  </div>
                </div>
              )}
              <div className="rcm-field">
                <label>Message (optional)</label>
                <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Anything else we should know?" />
              </div>
            </form>
          </>
        )}
      </div>

      <div className="rcm-footer">
        {submitted ? (
          inline ? (
            <a href="/" className="rcm-submit" style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}>
              Back to Home
            </a>
          ) : (
            <button className="rcm-submit" onClick={onClose}>Close</button>
          )
        ) : (
          <button type="submit" form="rcm-form" className="rcm-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        )}
      </div>
    </div>
  );

  if (inline) {
    return (
      <>
        <style>{styles}</style>
        {dialogContent}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="rcm-overlay" onClick={onClose}>
        {dialogContent}
      </div>
    </>
  );
};

export default ReserveCarModal;
